import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import '../../components/AppRules.css';
import '../people/People.css';

const API = process.env.REACT_APP_API_URL;
const errorOf = (e, fallback) => e?.response?.data?.error || fallback;

/**
 * Settings -> People & access -> Joiners & leavers (113).
 *
 *   #auto       when a checklist starts by itself: somebody added here, or
 *               deactivated here - each a switch, on until turned off
 *   #inactive   how long without signing in before somebody is listed
 *               under People -> Not signed in
 *   #joiner     the joiner checklist: its steps, each a working-day offset
 *   #leaver     from the start date, or back from the leaving date
 *
 * Editing a checklist changes the next one started; a checklist already
 * open keeps the steps it was given.
 */

/** One checklist's editor: a list of steps, saved as a whole. */
function TemplateEditor({ kind, initial, onSaved }) {
  const [steps, setSteps] = useState(() => (initial?.steps || []).map((s) => ({ ...s, offset: s.offset ?? '' })));
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');
  const [said, setSaid] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setSteps((initial?.steps || []).map((s) => ({ ...s, offset: s.offset ?? '' })));
    setDirty(false);
  }, [initial]);

  const change = (i, patch) => { setSteps((list) => list.map((s, n) => (n === i ? { ...s, ...patch } : s))); setDirty(true); setSaid(''); };
  const move = (i, by) => {
    setSteps((list) => {
      const to = i + by;
      if (to < 0 || to >= list.length) return list;
      const next = [...list];
      [next[i], next[to]] = [next[to], next[i]];
      return next;
    });
    setDirty(true); setSaid('');
  };
  const remove = (i) => { setSteps((list) => list.filter((_, n) => n !== i)); setDirty(true); setSaid(''); };
  const add = () => {
    setSteps((list) => [...list, { title: '', section: list.length ? list[list.length - 1].section : '', offset: 0, notes: '' }]);
    setDirty(true); setSaid('');
  };

  const save = async () => {
    setSaving(true); setProblem(''); setSaid('');
    try {
      const body = steps.map((s) => ({
        id: s.id, title: s.title, section: s.section || null, notes: s.notes || null,
        offset: s.offset === '' || s.offset === null || s.offset === undefined ? null : Number(s.offset)
      }));
      const r = await axios.put(`${API}/people/templates/${kind}`, { steps: body });
      onSaved(kind, r.data);
      setSaid(`Saved. The next ${kind} checklist started gets these ${r.data.steps.length} steps.`);
    } catch (e) {
      setProblem(errorOf(e, 'Could not save the checklist.'));
    } finally { setSaving(false); }
  };

  const fromWord = kind === 'joiner' ? 'the start date' : 'the leaving date';
  return (
    <section className="stn-card" id={`s-${kind}`} aria-labelledby={`pp-tpl-${kind}`}>
      <h3 className="stn-card-title" id={`pp-tpl-${kind}`}>{kind === 'joiner' ? 'The joiner checklist' : 'The leaver checklist'}</h3>
      <p className="stn-help">
        Day is in working days from {fromWord} &mdash; negative for before it, 0 for the day itself, blank for no
        date. Weekends and Malta&rsquo;s public holidays are skipped.
        {initial?.updated_by_name ? ` Last saved by ${initial.updated_by_name}.` : ''}
      </p>
      <ol className="pp-tpl" aria-label={`${kind} steps`}>
        {steps.map((s, i) => (
          <li key={s.id || `new-${i}`} className="pp-tpl-row" data-tpl-step={i}>
            <label className="pp-field pp-tpl-title">
              <span>Step {i + 1}</span>
              <input type="text" value={s.title} maxLength={300} aria-label={`Step ${i + 1}`}
                     onChange={(e) => change(i, { title: e.target.value })} />
            </label>
            <label className="pp-field">
              <span>Section</span>
              <input type="text" value={s.section || ''} maxLength={80} aria-label={`Section of step ${i + 1}`}
                     onChange={(e) => change(i, { section: e.target.value })} />
            </label>
            <label className="pp-field">
              <span>Day</span>
              <input type="number" step="1" min="-365" max="365" value={s.offset} aria-label={`Day of step ${i + 1}`}
                     onChange={(e) => change(i, { offset: e.target.value })} />
            </label>
            <div className="pp-tpl-tools">
              <button type="button" className="pp-icon-btn" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move step ${i + 1} up`}>↑</button>
              <button type="button" className="pp-icon-btn" disabled={i === steps.length - 1} onClick={() => move(i, 1)} aria-label={`Move step ${i + 1} down`}>↓</button>
              <button type="button" className="pp-icon-btn" onClick={() => remove(i)} aria-label={`Remove step ${i + 1}`}>✕</button>
            </div>
            <label className="pp-field pp-tpl-notes">
              <span>How (optional)</span>
              <input type="text" value={s.notes || ''} maxLength={4000} aria-label={`How to do step ${i + 1}`}
                     onChange={(e) => change(i, { notes: e.target.value })} />
            </label>
          </li>
        ))}
      </ol>
      {problem && <p className="pp-problem" role="alert">{problem}</p>}
      <p className="pp-flash" role="status" aria-live="polite">{said}</p>
      <div className="stn-actions">
        <button type="button" className="btn btn-secondary" onClick={add}>Add a step</button>
        <button type="button" className="btn btn-primary" disabled={saving || !dirty} onClick={save}>
          {saving ? 'Saving…' : `Save the ${kind} checklist`}
        </button>
      </div>
    </section>
  );
}

export default function PeopleSettingsPage() {
  const [rules, setRules] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [problem, setProblem] = useState('');
  const [needs, setNeeds] = useState(false);
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, t] = await Promise.all([axios.get(`${API}/people/settings`), axios.get(`${API}/people/templates`)]);
      setRules(r.data); setTemplates(t.data); setProblem(''); setNeeds(false);
    } catch (e) {
      if (e?.response?.status === 503) setNeeds(true);
      setProblem(errorOf(e, 'Could not load the joiner and leaver settings.'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRule = async (key, value) => {
    setSaving(key); setProblem('');
    try {
      const r = await axios.put(`${API}/people/settings`, { [key]: value });
      setRules(r.data);
    } catch (e) {
      setProblem(errorOf(e, 'Could not save that.'));
    } finally { setSaving(null); }
  };

  if (needs) {
    return (
      <div className="stn-card">
        <p className="stn-help">Joiners and leavers need migration 113 on this server. It runs with the next deploy (<code>npm run migrate</code>).</p>
      </div>
    );
  }
  if (!rules || !templates) return problem ? <p className="pp-problem" role="alert">{problem}</p> : <p className="stn-quiet">Loading…</p>;

  return (
    <div className="pp-settings">
      {problem && <p className="pp-problem" role="alert">{problem}</p>}
      <div className="ar pp-rules">
        <div className="ar-rule" id="s-auto">
          <label className="ar-check">
            <input type="checkbox" checked={rules.joiner_auto !== false} disabled={saving === 'joiner_auto'}
                   onChange={() => setRule('joiner_auto', rules.joiner_auto === false)} />
            <span className="ar-label">Start a joiner checklist when somebody is added in Users</span>
          </label>
          <p className="ar-consequence">
            {rules.joiner_auto !== false
              ? 'On: adding a person in Settings → Users starts their joiner checklist, owned by whoever added them. The form can still say not to, one person at a time.'
              : 'Off: start one by hand from Advanced → Joiners & leavers.'}
          </p>
          <label className="ar-check pp-rule-gap">
            <input type="checkbox" checked={rules.leaver_auto !== false} disabled={saving === 'leaver_auto'}
                   onChange={() => setRule('leaver_auto', rules.leaver_auto === false)} />
            <span className="ar-label">Start a leaver checklist when somebody is deactivated</span>
          </label>
          <p className="ar-consequence">
            {rules.leaver_auto !== false
              ? 'On: deactivating a person starts their leaver checklist, with the devices, desk and approvals still theirs listed live on it.'
              : 'Off: start one by hand. Asset Portal → Leavers still lists what deactivated people hold.'}
          </p>
        </div>
        <div className="ar-rule pp-rule-gap" id="s-inactive">
          <p className="ar-label">Not signed in</p>
          <div className="ar-numbers">
            <label className="ar-number">
              <span>List people who have not signed in for</span>
              <input type="number" min="1" max="365" step="1" defaultValue={rules.inactive_days} key={`d-${rules.inactive_days}`}
                     disabled={saving === 'inactive_days'} aria-label="Days without signing in"
                     onBlur={(e) => Number(e.target.value) !== rules.inactive_days && setRule('inactive_days', Number(e.target.value))} />
              <span>days</span>
            </label>
          </div>
          <p className="ar-consequence">
            Shown under <Link to="/people/inactive">Joiners &amp; leavers → Not signed in</Link>: usually somebody who has
            left without anybody telling IT.
          </p>
        </div>
      </div>
      <TemplateEditor kind="joiner" initial={templates.joiner} onSaved={(k, t) => setTemplates((x) => ({ ...x, [k]: t }))} />
      <TemplateEditor kind="leaver" initial={templates.leaver} onSaved={(k, t) => setTemplates((x) => ({ ...x, [k]: t }))} />
    </div>
  );
}
